import {
  get,
  getHgproBaseurl,
  LogErrorMessage,
  setSelfServeApiToken,
} from '../service';

export const AUTH_STATUS = Object.freeze({
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
});

export const AUTH_CATEGORIES = Object.freeze({
  ADMIN: 'ADMIN',
  PROVIDER: 'PROVIDER',
  UNSUPPORTED: 'UNSUPPORTED',
  UNKNOWN: 'UNKNOWN',
});

/** @typedef {typeof AUTH_STATUS[keyof typeof AUTH_STATUS]} AuthStatus */
/** @typedef {'Elevated_Profile_Admin' | 'Roster_Admin' | 'HG_Admin' | 'Client_Admin' | 'Facility_Manager' | 'Provider' | 'Roster'} BackendUserType */
/** @typedef {keyof typeof AUTH_CATEGORIES} UserCategory */

/**
 * Maps backend user types to frontend categories
 * @type {Record<BackendUserType, UserCategory>}
 */
const USER_TYPE_TO_CATEGORY = Object.freeze({
  Elevated_Profile_Admin: AUTH_CATEGORIES.ADMIN,
  Roster_Admin: AUTH_CATEGORIES.ADMIN,
  HG_Admin: AUTH_CATEGORIES.ADMIN,
  Facility_Manager: AUTH_CATEGORIES.ADMIN,
  Provider: AUTH_CATEGORIES.PROVIDER,
  Client_Admin: AUTH_CATEGORIES.UNSUPPORTED,
  Roster: AUTH_CATEGORIES.UNSUPPORTED,
});

/**
 * Converts backend userType to frontend userCategory
 * @param {BackendUserType | string} [userType] - Backend user type
 * @returns {UserCategory} Frontend user category
 */
const classifyBackendUserType = (userType) => {
  if (!userType) {
    return AUTH_CATEGORIES.UNKNOWN;
  }

  /** @type UserCategory */
  const category = USER_TYPE_TO_CATEGORY[userType];

  if (!category) {
    console.warn(
      `Unknown user type ("${userType}") - treating as \`UNKNOWN\` category`,
    );
    return AUTH_CATEGORIES.UNKNOWN;
  }

  return category;
};

/**
 * @typedef {Object} AuthenticatedValidateSessionResponse
 * @property {true} isAuthenticated
 * @property {string} userId - Unique user identifier
 * @property {string} userFirstName - User's first name
 * @property {string} userLastName - User's last name
 * @property {string} userEmail - User's email address
 * @property {string} pwid - Provider Web ID (empty string for non-provider users)
 * @property {BackendUserType} userType - User role type from backend
 * @property {string} [apiToken] - JWT for self-serve API Gateway
 * @property {string} [apiTokenExpiresAt] - UTC expiry for apiToken
 *
 * @typedef {Object} UnauthenticatedValidateSessionResponse
 * @property {false} isAuthenticated
 *
 * @typedef {AuthenticatedValidateSessionResponse | UnauthenticatedValidateSessionResponse} ValidateSessionResponse
 */

/**
 * @typedef {Object} AuthUser
 * @property {string} userId
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} fullName
 * @property {string} email
 * @property {string} pwid
 */

/**
 * @typedef {Object} LoadingAuthSession
 * @property {typeof AUTH_STATUS.LOADING} status
 * @property {typeof AUTH_CATEGORIES.UNKNOWN} userCategory
 * @property {typeof AUTH_CATEGORIES.UNKNOWN} userType
 * @property {null} user
 *
 * @typedef {Object} UnauthenticatedAuthSession
 * @property {typeof AUTH_STATUS.UNAUTHENTICATED} status
 * @property {typeof AUTH_CATEGORIES.UNKNOWN} userCategory
 * @property {typeof AUTH_CATEGORIES.UNKNOWN} userType
 * @property {null} user
 *
 * @typedef {Object} AuthenticatedAuthSession
 * @property {typeof AUTH_STATUS.AUTHENTICATED} status
 * @property {UserCategory} userCategory
 * @property {BackendUserType} userType
 * @property {AuthUser} user
 *
 * @typedef {LoadingAuthSession | UnauthenticatedAuthSession | AuthenticatedAuthSession} AuthSession
 */

/**
 * @typedef {null} KeepSessionAliveResponse
 */

/** @type {LoadingAuthSession} */
export const LOADING_AUTH_SESSION = Object.freeze({
  status: AUTH_STATUS.LOADING,
  userCategory: AUTH_CATEGORIES.UNKNOWN,
  userType: 'UNKNOWN',
  user: null,
});

/** @type {UnauthenticatedAuthSession} */
export const UNAUTHENTICATED_SESSION = Object.freeze({
  status: AUTH_STATUS.UNAUTHENTICATED,
  userCategory: AUTH_CATEGORIES.UNKNOWN,
  userType: 'UNKNOWN',
  user: null,
});

/**
 * Creates a fake authenticated session for development when auth is disabled
 * @returns {AuthenticatedAuthSession}
 */
const createFakeAuthSession = () => {
  /** @type {AuthenticatedAuthSession} */
  const fakeSession = {
    status: AUTH_STATUS.AUTHENTICATED,
    userCategory: AUTH_CATEGORIES.ADMIN,
    userType: 'Elevated_Profile_Admin',
    user: {
      userId: 'fake-user-id-123',
      firstName: 'Test',
      lastName: 'User',
      fullName: 'Test User',
      email: 'test.user@example.com',
      pwid: '1234567890', // Set to '' to test admin flow
    },
  };

  return fakeSession;
};

const applySelfServeApiToken = (response) => {
  if (response?.isAuthenticated === false) {
    setSelfServeApiToken(null);
    return;
  }

  if (response?.apiToken) {
    setSelfServeApiToken(response.apiToken);
    return;
  }

  // Authenticated but no token (e.g. JWT secret not configured) — do not send a stale Bearer token.
  if (response?.isAuthenticated) {
    setSelfServeApiToken(null);
  }
};

/**
 * Loads the authentication state for the current user session.
 * @param {Object} [options]
 * @param {boolean} [options.useFakeAuth] - Use fake auth for development (default: false)
 * @returns {Promise<AuthSession>}
 */
export const fetchAuthSession = async (options = {}) => {
  const { useFakeAuth = false } = options;

  // Return fake auth session for development if requested
  if (useFakeAuth && process.env.NODE_ENV === 'development') {
    console.warn(
      '🔐⚠️ Using fake auth session for development. To test provider flow, modify createFakeAuthSession() in account.js',
    );
    return createFakeAuthSession();
  }

  const _url = getHgproBaseurl(window.location.hostname);

  try {
    /** @type {ValidateSessionResponse} */
    const response = await get(
      `${_url}/api/account/validateSessionForSelfServeAndGetAuth`,
    );

    applySelfServeApiToken(response);

    // If not authenticated, return early
    if (!response?.isAuthenticated) {
      return UNAUTHENTICATED_SESSION;
    }

    const fullName =
      [response.userFirstName, response.userLastName]
        .filter(Boolean)
        .join(' ')
        .trim() || 'Unknown User';

    const userCategory = classifyBackendUserType(response.userType);

    /** @type {AuthenticatedAuthSession} */
    const authenticatedSession = {
      status: AUTH_STATUS.AUTHENTICATED,
      userCategory,
      userType: response.userType,
      user: {
        userId: response.userId,
        firstName: response.userFirstName,
        lastName: response.userLastName,
        fullName,
        email: response.userEmail,
        pwid: response.pwid,
      },
    };

    return authenticatedSession;
  } catch (error) {
    console.error('Failed to fetch auth session:', error);
    setSelfServeApiToken(null);
    LogErrorMessage(error);

    return UNAUTHENTICATED_SESSION;
  }
};

/**
 * Keeps the user's session alive by calling the keep-alive endpoint.
 * @returns {Promise<KeepSessionAliveResponse>}
 */
export const keepSessionAlive = async () => {
  const baseUrl = getHgproBaseurl(window.location.hostname);

  try {
    await get(`${baseUrl}/api/session/keepSessionLive`);

    const sessionResponse = await get(
      `${baseUrl}/api/account/validateSessionForSelfServeAndGetAuth`,
    );
    applySelfServeApiToken(sessionResponse);

    return null;
  } catch (error) {
    console.error('Failed to keep session alive:', error);
    setSelfServeApiToken(null);
    LogErrorMessage(error);
    return null;
  }
};
